import React, { useState, useEffect } from 'react';
import {
  Dropdown,
  TextInput,
  TextArea,
  Button,
  Link,
  Form,
  InlineLoading
} from 'carbon-components-react';
import { useFormik } from 'formik';
import { FormError } from 'components';
import { SendFeedbackSchema } from 'utilities/validation/sendFeedback.validation';
import Utils from 'utilities/helper.util';

interface FormValues {
  feedbackType: string;
  subject: string;
  description: string;
}

const formInitialValues = {
  feedbackType: '',
  subject: '',
  description: ''
};

const feedbackTypeItem = [
  {
    label: 'Support',
    value: 'support'
  },
  {
    label: 'User experience',
    value: 'user_experience'
  },
  {
    label: 'Performance',
    value: 'performance'
  },
  {
    label: 'Other',
    value: 'other'
  }
];

const {
  REACT_APP_HELP_FAQ_URL,
  REACT_APP_SWX_SUPPORT_URL,
  REACT_APP_ESRI_AUSTRALIA_SUPPORT_EMAIL,
  REACT_APP_SUPPORT_PHONE_NUMBER
} = process.env;

const FeedbackForm: React.FC<Props> = ({
  onSubmit,
  handleCancel,
  isSendingFeedback
}) => {
  const {
    touched,
    errors,
    values,
    setFieldValue,
    handleSubmit,
    handleChange,
    handleBlur
  } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema: SendFeedbackSchema,
    onSubmit: (values: FormValues) => {
      onSubmit(values);
    }
  });

  const [isFormFieldsDirty, setIsFormFieldsDirty] = useState<boolean>(true);
  const [showSupportContent, setShowSupportContent] = useState<boolean>(false);

  const handleDropdownChange = (key: any, { selectedItem }: any) => {
    if (selectedItem?.value) {
      setFieldValue(key, selectedItem.value);
      setShowSupportContent(selectedItem.value === 'support');
      if (selectedItem.value === 'support') {
        setFieldValue('subject', '');
        setFieldValue('description', '');
      }
    }
  };

  useEffect(() => {
    let isDirty = true;
    const touchedFields = Utils.isObjectEmpty(touched);
    if (!touchedFields) {
      isDirty = Object.values(values).filter((v) => v === '').length > 0;
      setIsFormFieldsDirty(isDirty);
    }
  }, [errors, values, touched]);

  return (
    <div>
      <div className="feedback-form">
        <Form className="modal-form--wrapper" onSubmit={handleSubmit}>
          <div className="feedback-form-fields spacing">
            <section className="feedback-form-fields-item">
              <Dropdown
                id="feedbackType"
                titleText="Type of feedback"
                label="Choose an option"
                items={feedbackTypeItem}
                itemToString={(item: any) => item.label}
                onBlur={handleBlur}
                onChange={(val) => handleDropdownChange('feedbackType', val)}
              />
              {errors.feedbackType && touched.feedbackType && (
                <FormError>{errors.feedbackType}</FormError>
              )}
            </section>
            {!showSupportContent && (
              <>
                <section className="feedback-form-fields-item">
                  <TextInput
                    id="subject"
                    name="subject"
                    labelText="Subject"
                    placeholder="Your input"
                    maxLength={150}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values?.subject || ''}
                  />
                  {errors.subject && touched.subject && (
                    <FormError>{errors.subject}</FormError>
                  )}
                </section>
                <section className="feedback-form-fields-item">
                  <TextArea
                    cols={50}
                    id="description"
                    name="description"
                    labelText="What's on your mind?"
                    placeholder="Your input"
                    helperText="500 characters maximum"
                    rows={5}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={500}
                  />
                  {errors.description && touched.description && (
                    <FormError>{errors.description}</FormError>
                  )}
                </section>
              </>
            )}
            {showSupportContent && (
              <>
                <section className="feedback-form-fields-item">
                  <div className="wording-styling">
                    Simple technical issue may be covered by our{' '}
                    <Link href={REACT_APP_HELP_FAQ_URL} target="_blank">
                      Help and FAQs
                    </Link>
                    .
                  </div>
                  <div className="wording-styling">
                    For all other technical support see{' '}
                    <Link href={REACT_APP_SWX_SUPPORT_URL} target="_blank">
                      SmarterWX Support
                    </Link>
                    .
                  </div>
                </section>
                <section className="feedback-form-fields-item">
                  <div className="wording-styling">
                    You can also call us on{' '}
                    <span className="support-number">
                      {REACT_APP_SUPPORT_PHONE_NUMBER}
                    </span>{' '}
                    or submit your support request by email here{' '}
                    <Link
                      href={`mailto:${REACT_APP_ESRI_AUSTRALIA_SUPPORT_EMAIL}`}
                      target="_blank"
                    >
                      {REACT_APP_ESRI_AUSTRALIA_SUPPORT_EMAIL}
                    </Link>
                    .
                  </div>
                </section>
              </>
            )}
          </div>

          <div className="feedback-form-buttons">
            {!showSupportContent && (
              <>
                <Button
                  id="cancel"
                  name="cancel"
                  kind="tertiary"
                  data-testid="cancel"
                  onClick={() => handleCancel()}
                >
                  Cancel
                </Button>
                <Button
                  id="sendFeedback"
                  name="sendFeedback"
                  type="submit"
                  kind="secondary"
                  data-testid="submit"
                  disabled={
                    isFormFieldsDirty ||
                    !Utils.isObjectEmpty(errors) ||
                    isSendingFeedback
                  }
                >
                  {!isSendingFeedback ? (
                    'Send feedback'
                  ) : (
                    <InlineLoading
                      description={
                        !isSendingFeedback ? 'Send feedback' : 'Sending...'
                      }
                      status={!isSendingFeedback ? 'finished' : 'active'}
                      aria-live={'assertive'}
                    />
                  )}
                </Button>
              </>
            )}
            {showSupportContent && (
              <Button
                id="close"
                name="close"
                kind="secondary"
                data-testid="close"
                onClick={() => handleCancel()}
                className="close-button"
              >
                Close
              </Button>
            )}
          </div>
        </Form>
      </div>
    </div>
  );
};

type Props = {
  onSubmit: Function;
  handleCancel: Function;
  isSendingFeedback: boolean;
};

export default React.memo(FeedbackForm);
